import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { useForm, FormProvider, useFieldArray, useFormContext } from "react-hook-form";

import Tabs from "components/common/Tabs";
import Text from "components/common/Text";
import CustomDatePicker from "components/common/DatePicker";
import Button from "components/common/Button";

import { CloseOutlined, Add } from "@material-ui/icons";

import "./DependentsModalContent.scss";

function ParentsContent() {
  return (
    <div className="flex flex-col gap-1">
      <div className="dependents-modal-content-wrapper">
        <Text label="Father name" name="fatherName" placeholder="e.g. John Smith" />
        <Text label="Father National ID" name="fatherNationalID" placeholder="CNIC, DNI, etc." />
      </div>
      <div className="dependents-modal-content-wrapper">
        <Text label="Mother name" name="motherName" placeholder="e.g. Jane Smith" />
        <Text label="Mother National ID" name="motherNationalID" placeholder="CNIC, DNI, etc." />
      </div>
    </div>
  );
}

function SpouseContent() {
  return (
    <div className="dependents-modal-content-wrapper">
      <Text label="Name" name="spouseName" placeholder="e.g. John Smith" />
      <div className="flex gap-075 items-end w-full">
        <Text label="National ID" name="spouseNationalID" placeholder="CNIC, DNI, etc." />
        <CustomDatePicker name="spouseDateOfBirth" label="Date of birth" />
      </div>
    </div>
  );
}

function ChildrenContent() {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "children",
  });

  const handleAddChild = () => {
    append({
      name: "",
      nationalID: "",
      dateOfBirth: "",
    });
  };

  const handleRemoveChild = (index) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      {fields.map((field, index) => (
        <div key={field.id} className="dependents-modal-content-wrapper">
          <div className="flex">
            <div className="flex flex-col gap-05 w-full">
              <Text label="Name" name={`children.${index}.name`} placeholder="e.g. John Smith" />
              <div className="flex gap-075 items-end w-full">
                <Text label="National ID" name={`children.${index}.nationalID`} placeholder="CNIC, DNI, etc." />
                <CustomDatePicker name={`children.${index}.dateOfBirth`} label="Date of birth" />
              </div>
            </div>
            {fields.length > 1 && (
              <div className="mt-15 p-05 cursor-pointer" onClick={() => handleRemoveChild(index)}>
                <CloseOutlined className="children-close-icon" />
              </div>
            )}
          </div>
        </div>
      ))}
      <div className="mt-05">
        <Button variant="text" size="small" label="Add another child" endIcon={<Add />} onClick={handleAddChild} />
      </div>
    </div>
  );
}

const DependentsModalContent = forwardRef(({ initialData, initialTab = 0 }, ref) => {
  const [tab, setTab] = useState(initialTab);

  const defaultValues = {
    fatherName: "",
    fatherNationalID: "",
    motherName: "",
    motherNationalID: "",
    spouseName: "",
    spouseNationalID: "",
    spouseDateOfBirth: "",
    children: [
      {
        name: "",
        nationalID: "",
        dateOfBirth: "",
      },
    ],
  };

  const methods = useForm({
    mode: "onChange",
    defaultValues: initialData || defaultValues,
  });

  useEffect(() => {
    if (initialData) {
      methods.reset(initialData);
    } else {
      methods.reset(defaultValues);
    }
    setTab(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, initialTab]);

  useImperativeHandle(ref, () => ({
    getFormData: () => {
      return methods.getValues();
    },
  }));

  const handleTabChange = (value) => {
    setTab(value);
  };

  const renderTabContent = () => {
    switch (tab) {
      case 0:
        return <ParentsContent />;
      case 1:
        return <SpouseContent />;
      default:
        return <ChildrenContent />;
    }
  };

  return (
    <FormProvider {...methods}>
      <div>
        <Tabs
          tabs={[{ label: "Parents" }, { label: "Spouse" }, { label: "Children" }]}
          tab={tab}
          setTab={handleTabChange}
        />
        <div className="pt-1">{renderTabContent()}</div>
      </div>
    </FormProvider>
  );
});

DependentsModalContent.displayName = "DependentsModalContent";

export default DependentsModalContent;
